import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  Alert,
  Button,
  Loader,
  Menu,
  Pagination,
  Popover,
  SegmentedControl,
  Select,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconArrowLeft,
  IconCheck,
  IconChevronDown,
  IconDownload,
  IconFilter,
  IconMapPin,
  IconPlus,
  IconSpeakerphone,
  IconUser,
  IconX,
} from '@tabler/icons-react'
import {
  CRM_GENDERS,
  CRM_LEAD_SOURCES,
  CRM_LEAD_STATUSES,
  CRM_NAME_PATTERN,
  CRM_POSTAL_CODE_PATTERN,
  type CrmGender,
  type CrmLeadSource,
  type CrmLeadStatus,
} from '@behuman/core-contract'
import { states } from '@/utils/states'
import {
  LEAD_PAGE_SIZE,
  useCreateLead,
  useCrmLeads,
  useImportPatients,
  useLeadCampaigns,
  useSetLeadNotes,
  useSetLeadStatus,
  type Lead,
  type LeadMilestone,
  type LeadTouch,
} from './hooks'
import { CampaignsTab, DeliveryChips } from './CampaignsTab'
import { ReportsTab, type ReportDrill } from './ReportsTab'
import { CrmModal, CrmSection } from './CrmModal'
import {
  CORAL,
  CrmCard,
  CrmChip,
  CrmIconTile,
  CrmSearchInput,
  LeadAvatar,
  LeadStatusBadge,
  NameButton,
  tableHeaderClass,
  tableRowClass,
} from './CrmUi'
import {
  ATTRIBUTION_FIELD_LABELS,
  CHANNEL_LABELS,
  errorMessage,
  GENDER_LABELS,
  LEAD_MILESTONE_LABELS,
  LEAD_SOURCE_LABELS,
  LEAD_STATUS_LABELS,
} from './labels'

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

const leadName = (lead: Lead) =>
  [lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'No name yet'

const monthYearFormat = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' })

/**
 * A birth date as a local date, e.g. "May 1, 1990". A chart's FHIR date may stop at the year
 * ("1990") or the month ("May 1990"); anything else is shown as stored.
 */
const birthDateLabel = (birthDate: string) => {
  const [year, month, day] = birthDate.split('-').map(Number)
  const date = new Date(year, (month || 1) - 1, day || 1)
  if (Number.isNaN(date.getTime())) return birthDate
  if (!month) return String(year)
  return (day ? dateFormat : monthYearFormat).format(date)
}

const stateOptions = states.map((state) => ({ value: state.value, label: state.label }))

/** An unknown value in a table cell. */
const EmptyCell = () => <span className="text-[#9AA0A8]">—</span>

/** "Google Ads · fall-checkup", or the source alone when the visit had no campaign. */
function TouchCell({ touch }: { touch: LeadTouch }) {
  return (
    <div>
      <div className={touch.source === 'unknown' ? 'text-[#6B7280]' : 'text-[#1F2733]'}>
        {LEAD_SOURCE_LABELS[touch.source]}
      </div>
      {touch.campaign && <div className="text-xs text-[#6B7280]">{touch.campaign}</div>}
    </div>
  )
}

function TouchDetails({ touch }: { touch: LeadTouch }) {
  const fields = Object.entries(ATTRIBUTION_FIELD_LABELS).filter(
    ([key]) => touch.attribution?.[key as keyof typeof ATTRIBUTION_FIELD_LABELS],
  )
  return (
    <div className="flex flex-col gap-1">
      <div className="font-bold text-[#1F2733]">
        {LEAD_SOURCE_LABELS[touch.source]}
        {touch.campaign ? ` · ${touch.campaign}` : ''}
      </div>
      {touch.attribution?.capturedAt && (
        <div className="text-xs text-[#6B7280]">
          Seen {dateTimeFormat.format(new Date(touch.attribution.capturedAt))}
        </div>
      )}
      {fields.length > 0 ? (
        <Table mt={6} fz="xs" withRowBorders={false} verticalSpacing={2} horizontalSpacing={0}>
          <Table.Tbody>
            {fields.map(([key, label]) => (
              <Table.Tr key={key}>
                <Table.Td className="w-36 text-[#6B7280]">{label}</Table.Td>
                <Table.Td className="break-all text-[#1F2733]">
                  {touch.attribution?.[key as keyof typeof ATTRIBUTION_FIELD_LABELS]}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        // Manual / Import and Zocdoc leads never came through a visit: their source says it all.
        touch.source !== 'manual' &&
        touch.source !== 'zocdoc' && (
          <div className="mt-1 text-xs text-[#6B7280]">
            No tracking was captured for this visit.
          </div>
        )
      )}
    </div>
  )
}

/** Staff change a lead's status by hand — e.g. Not interested once someone asks us to stop. */
function useChangeStatus(lead: Lead, onChanged?: (lead: Lead) => void) {
  const setStatus = useSetLeadStatus()
  const change = (status: CrmLeadStatus) =>
    status !== lead.status &&
    setStatus.mutate(
      { leadId: lead.id, status },
      {
        onSuccess: (changed) => onChanged?.(changed),
        onError: () => notifications.show({ color: 'red', message: 'Couldn’t change the status' }),
      },
    )
  return { change, pending: setStatus.isPending }
}

const STATUS_HELP =
  'Appointments move it on their own, except Not interested and Completed appointment, which only staff change. Campaigns never reach Not interested.'

function LeadStatusSelect({ lead, onChanged }: { lead: Lead; onChanged: (lead: Lead) => void }) {
  const { change, pending } = useChangeStatus(lead, onChanged)
  return (
    <Select
      aria-label="Status"
      radius="md"
      data={CRM_LEAD_STATUSES.map((value) => ({ value, label: LEAD_STATUS_LABELS[value] }))}
      value={lead.status}
      allowDeselect={false}
      disabled={pending}
      onChange={(value) => value && change(value as CrmLeadStatus)}
      description={STATUS_HELP}
    />
  )
}

/** The status chip in a table row, opening a menu to change it without leaving the list. */
function LeadStatusMenu({ lead }: { lead: Lead }) {
  const { change, pending } = useChangeStatus(lead)
  return (
    <Menu position="bottom-start" shadow="md" radius="md" withinPortal>
      <Menu.Target>
        <button
          type="button"
          aria-label={`Status: ${LEAD_STATUS_LABELS[lead.status]}. Change`}
          disabled={pending}
          onClick={(event) => event.stopPropagation()}
          className="inline-flex cursor-pointer items-center gap-1 rounded-full border-0 bg-transparent p-0"
        >
          <LeadStatusBadge status={lead.status} />
          <IconChevronDown size={14} className="text-[#9AA0A8]" aria-hidden />
        </button>
      </Menu.Target>
      <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
        {CRM_LEAD_STATUSES.map((status) => (
          <Menu.Item
            key={status}
            onClick={() => change(status)}
            rightSection={status === lead.status ? <IconCheck size={14} /> : null}
          >
            {LEAD_STATUS_LABELS[status]}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )
}

/** The notes text box and its Save; used in the row's popover and the lead dialog. */
function NotesEditor({ lead, onSaved }: { lead: Lead; onSaved?: (lead: Lead) => void }) {
  const [notes, setNotes] = useState(lead.notes ?? '')
  const save = useSetLeadNotes()
  const dirty = notes.trim() !== (lead.notes ?? '')
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate(
          { leadId: lead.id, notes },
          {
            onSuccess: (saved) => onSaved?.(saved),
            onError: () => notifications.show({ color: 'red', message: 'Couldn’t save the notes' }),
          },
        )
      }}
    >
      <Textarea
        aria-label={`Notes on ${leadName(lead)}`}
        placeholder="Calls, what they said, when to follow up…"
        radius="md"
        autosize
        minRows={3}
        maxRows={10}
        maxLength={5000}
        value={notes}
        onChange={(event) => setNotes(event.currentTarget.value)}
      />
      <Button
        type="submit"
        size="xs"
        radius="md"
        className="self-end"
        disabled={!dirty}
        loading={save.isPending}
      >
        Save notes
      </Button>
    </form>
  )
}

/** The last column: the lead's notes, edited in place without opening the lead. */
function NotesCell({ lead }: { lead: Lead }) {
  const [opened, setOpened] = useState(false)
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={320}
      shadow="md"
      radius="md"
      withinPortal
      trapFocus
    >
      <Popover.Target>
        <button
          type="button"
          aria-label={
            lead.notes ? `Edit notes on ${leadName(lead)}` : `Add notes on ${leadName(lead)}`
          }
          onClick={(event) => {
            event.stopPropagation()
            setOpened((open) => !open)
          }}
          className="line-clamp-2 max-w-[220px] cursor-pointer border-0 bg-transparent p-0 text-left text-[13px] text-[#4A5160] hover:text-[#1F2733]"
        >
          {lead.notes || <span className="text-[#9AA0A8]">Add note</span>}
        </button>
      </Popover.Target>
      <Popover.Dropdown onClick={(event) => event.stopPropagation()}>
        <NotesEditor lead={lead} onSaved={() => setOpened(false)} />
      </Popover.Dropdown>
    </Popover>
  )
}

/** The campaigns the lead is in, newest first, and what happened to their message. */
function LeadCampaigns({ leadId }: { leadId: number }) {
  const campaigns = useLeadCampaigns(leadId)
  if (campaigns.isError) {
    return (
      <Text size="sm" c="red">
        Couldn’t load the campaigns.
      </Text>
    )
  }
  if (campaigns.isPending) return <Loader size="sm" aria-label="Loading the campaigns" />
  const rows = campaigns.data.data
  const total = campaigns.data.page.total
  if (!rows.length) {
    return <p className="text-sm text-[#6B7280]">Not in any campaign yet.</p>
  }
  return (
    <div className="flex flex-col gap-2">
      <ul aria-label="Campaigns" className="divide-y divide-[#F0F1F3]">
        {rows.map((campaign) => (
          <li key={campaign.campaignId} className="flex items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-bold text-[#1F2733]">{campaign.name}</div>
              <div className="text-xs text-[#6B7280]">
                {CHANNEL_LABELS[campaign.channel]} ·{' '}
                {campaign.launchedAt
                  ? `Launched ${dateFormat.format(new Date(campaign.launchedAt))}`
                  : `Added ${dateFormat.format(new Date(campaign.addedAt))}`}
              </div>
            </div>
            {campaign.deliveries.length ? (
              <DeliveryChips deliveries={campaign.deliveries} />
            ) : (
              <CrmChip color="gray">Draft</CrmChip>
            )}
          </li>
        ))}
      </ul>
      {total > rows.length && (
        <span className="text-xs text-[#6B7280]">
          Showing the latest {rows.length} of {total}.
        </span>
      )}
    </div>
  )
}

/** One labelled value in the lead's details; a dash when unknown. */
function Detail({ label, value, wide }: { label: string; value: ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'col-span-2' : undefined}>
      <dt className="text-[13px] font-semibold text-[#6B7280]">{label}</dt>
      <dd className="m-0 mt-0.5 break-words text-[#1F2733]">
        {value || <span className="text-[#9AA0A8]">—</span>}
      </dd>
    </div>
  )
}

/** What we know of the person: entered with the lead, or copied from their chart. */
function RegistrationDetails({ lead }: { lead: Lead }) {
  const state = states.find((option) => option.value === lead.state)
  return (
    <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
      <Detail label="Date of birth" value={lead.birthDate && birthDateLabel(lead.birthDate)} />
      <Detail label="Gender" value={lead.gender && GENDER_LABELS[lead.gender]} />
      <Detail label="Phone" value={lead.phone} />
      <Detail
        label="Registered"
        value={lead.registeredAt && dateFormat.format(new Date(lead.registeredAt))}
      />
      <Detail label="Email" value={lead.email} wide />
      <Detail label="Address" value={lead.address} wide />
      <Detail label="ZIP" value={lead.postalCode} />
      <Detail label="State" value={state ? `${state.label} (${state.value})` : lead.state} />
      <Detail
        label="Patient chart"
        wide
        value={
          lead.patientId ? (
            <Link to={`/patients/${lead.patientId}`} className="font-semibold text-[#24548A]">
              Open patient chart
            </Link>
          ) : (
            'Not registered yet'
          )
        }
      />
    </dl>
  )
}

function LeadModal({
  lead,
  onClose,
  onChanged,
}: {
  lead: Lead | null
  onClose: () => void
  onChanged: (lead: Lead) => void
}) {
  return (
    <CrmModal
      opened={lead !== null}
      onClose={onClose}
      size={880}
      title={lead ? leadName(lead) : ''}
      icon={
        <CrmIconTile>
          <IconUser size={20} />
        </CrmIconTile>
      }
      badges={lead && <LeadStatusBadge status={lead.status} />}
      subtitle={lead && [lead.phone, lead.email].filter(Boolean).join(' · ')}
      footer={
        lead?.patientId && (
          <Button component={Link} to={`/patients/${lead.patientId}`} variant="default">
            Open patient chart
          </Button>
        )
      }
    >
      {lead && (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <div className="flex flex-col gap-5">
              <CrmSection title="Status">
                <LeadStatusSelect lead={lead} onChanged={onChanged} />
              </CrmSection>
              <CrmSection title="Timeline">
                <ol className="flex flex-col gap-2.5 text-sm">
                  {[
                    ['Added', lead.createdAt],
                    ['Phone confirmed', lead.confirmedAt],
                    ['Registered', lead.registeredAt],
                  ]
                    .filter((entry): entry is [string, string] => Boolean(entry[1]))
                    .map(([label, at]) => (
                      <li key={label} className="flex gap-3">
                        <span
                          aria-hidden
                          className="bg-primary mt-1.5 h-2 w-2 shrink-0 rounded-full"
                        />
                        <div>
                          <div className="font-semibold text-[#1F2733]">{label}</div>
                          <div className="text-xs text-[#6B7280]">
                            {dateTimeFormat.format(new Date(at))}
                          </div>
                        </div>
                      </li>
                    ))}
                </ol>
              </CrmSection>
            </div>
            <div className="flex flex-col gap-5">
              <CrmSection title="Registration details">
                <RegistrationDetails lead={lead} />
              </CrmSection>
              <CrmSection title="Source">
                <TouchDetails touch={lead.original} />
              </CrmSection>
            </div>
          </div>
          <CrmSection title="Notes">
            <NotesEditor key={lead.id} lead={lead} onSaved={onChanged} />
          </CrmSection>
          <CrmSection title="Campaigns">
            <LeadCampaigns leadId={lead.id} />
          </CrmSection>
        </>
      )}
    </CrmModal>
  )
}

const emptyLead = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  birthDate: '',
  gender: null as CrmGender | null,
  address: '',
  postalCode: '',
  state: null as string | null,
  source: 'zocdoc' as CrmLeadSource,
  campaign: '',
}

/** The Patient App's name rules, so a lead entered by hand reads like one who registered (PD-2099). */
const nameError = (value: string, label: string) => {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  if (trimmed.length < 2) return `${label} must be at least 2 characters`
  if (!CRM_NAME_PATTERN.test(trimmed))
    return `${label} can only include letters, spaces, apostrophes, and hyphens`
  return undefined
}

/** 5 digits, or ZIP+4 written 10001-1234, as the Patient App writes it. */
const formatZip = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 9)
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits
}

/** New lead: staff enter a lead by hand — most come from Zocdoc with all of these. */
function NewLeadButton({ onCreated }: { onCreated: (lead: Lead) => void }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyLead)
  const create = useCreateLead()
  const set =
    <K extends keyof typeof emptyLead>(key: K) =>
    (value: (typeof emptyLead)[K]) =>
      setForm((current) => ({ ...current, [key]: value }))
  const close = () => {
    setOpen(false)
    setForm(emptyLead)
    create.reset()
  }
  const blank = (value: string) => value.trim() || undefined
  const firstNameError = nameError(form.firstName, 'First name')
  const lastNameError = nameError(form.lastName, 'Last name')
  const zipError =
    form.postalCode && !CRM_POSTAL_CODE_PATTERN.test(form.postalCode)
      ? 'ZIP code must be 5 or 9 digits'
      : undefined
  const ready = Boolean(
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.phone.trim() &&
    !firstNameError &&
    !lastNameError &&
    !zipError,
  )
  const submit = () =>
    create.mutate(
      {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        email: blank(form.email),
        birthDate: form.birthDate || undefined,
        gender: form.gender ?? undefined,
        address: blank(form.address),
        postalCode: blank(form.postalCode),
        state: form.state ?? undefined,
        source: form.source,
        campaign: blank(form.campaign),
      },
      {
        onSuccess: (lead) => {
          close()
          notifications.show({ color: 'green', message: `Added ${leadName(lead)} as a lead.` })
          onCreated(lead)
        },
      },
    )
  return (
    <>
      <Button
        color={CORAL}
        radius="md"
        fw={700}
        leftSection={<IconPlus size={16} stroke={2.5} />}
        onClick={() => setOpen(true)}
      >
        New lead
      </Button>
      <CrmModal
        opened={open}
        onClose={close}
        title="New lead"
        subtitle="Enter someone who reached us outside the portal, such as a Zocdoc booking."
        size="lg"
        footer={
          <>
            <Button variant="default" radius="md" onClick={close}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="crm-new-lead"
              color={CORAL}
              radius="md"
              fw={700}
              disabled={!ready}
              loading={create.isPending}
            >
              Save lead
            </Button>
          </>
        }
      >
        <form
          id="crm-new-lead"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (ready) submit()
          }}
        >
          <TextInput
            label="First name"
            required
            radius="md"
            value={form.firstName}
            onChange={(event) => set('firstName')(event.currentTarget.value)}
            error={firstNameError}
          />
          <TextInput
            label="Last name"
            required
            radius="md"
            value={form.lastName}
            onChange={(event) => set('lastName')(event.currentTarget.value)}
            error={lastNameError}
          />
          <TextInput
            label="Phone"
            required
            type="tel"
            radius="md"
            placeholder="(646) 555-0101"
            value={form.phone}
            onChange={(event) => set('phone')(event.currentTarget.value)}
          />
          <TextInput
            label="Email"
            type="email"
            radius="md"
            value={form.email}
            onChange={(event) => set('email')(event.currentTarget.value)}
          />
          <TextInput
            label="Date of birth"
            type="date"
            radius="md"
            max={new Date().toISOString().slice(0, 10)}
            value={form.birthDate}
            onChange={(event) => set('birthDate')(event.currentTarget.value)}
          />
          <Select
            label="Gender"
            placeholder="Select gender"
            radius="md"
            clearable
            data={CRM_GENDERS.map((value) => ({ value, label: GENDER_LABELS[value] }))}
            value={form.gender}
            onChange={(value) => set('gender')(value as CrmGender | null)}
          />
          <TextInput
            label="Address"
            radius="md"
            placeholder="Street address, apartment / suite, city"
            value={form.address}
            onChange={(event) => set('address')(event.currentTarget.value)}
            className="sm:col-span-2"
          />
          <TextInput
            label="ZIP"
            radius="md"
            inputMode="numeric"
            maxLength={10}
            placeholder="10001"
            value={form.postalCode}
            onChange={(event) => set('postalCode')(formatZip(event.currentTarget.value))}
            error={zipError}
          />
          <Select
            label="State"
            placeholder="Select state"
            radius="md"
            searchable
            clearable
            data={stateOptions}
            value={form.state}
            onChange={set('state')}
          />
          <Select
            label="Source"
            radius="md"
            allowDeselect={false}
            data={CRM_LEAD_SOURCES.map((value) => ({ value, label: LEAD_SOURCE_LABELS[value] }))}
            value={form.source}
            onChange={(value) => value && set('source')(value as CrmLeadSource)}
          />
          <TextInput
            label="Campaign"
            description="Optional, e.g. fall_wellness"
            radius="md"
            value={form.campaign}
            onChange={(event) => set('campaign')(event.currentTarget.value)}
          />
          {create.isError && (
            <Alert color="red" variant="light" className="sm:col-span-2">
              {errorMessage(create.error, 'Couldn’t save the lead')}
            </Alert>
          )}
        </form>
      </CrmModal>
    </>
  )
}

/** Adds the project's existing patients as leads; safe to run again. */
function ImportPatientsButton() {
  const [open, setOpen] = useState(false)
  const importPatients = useImportPatients()
  const run = () =>
    importPatients.mutate(
      {},
      {
        onSuccess: (result) => {
          setOpen(false)
          notifications.show({
            color: 'green',
            message: `Added ${result.added} of ${result.scanned} patients as leads${
              result.skippedNoPhone ? ` (${result.skippedNoPhone} had no textable phone)` : ''
            }.`,
          })
        },
        onError: () =>
          notifications.show({ color: 'red', message: 'Couldn’t import the patients' }),
      },
    )
  return (
    <>
      <Button
        variant="default"
        radius="md"
        fw={600}
        leftSection={<IconDownload size={16} stroke={2.25} />}
        onClick={() => setOpen(true)}
      >
        Import existing patients
      </Button>
      <CrmModal
        opened={open}
        onClose={() => setOpen(false)}
        title="Import existing patients"
        size="md"
        footer={
          <>
            <Button variant="default" radius="md" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              color={CORAL}
              radius="md"
              fw={700}
              loading={importPatients.isPending}
              onClick={run}
            >
              Import
            </Button>
          </>
        }
      >
        <Text size="sm">
          Every patient with a phone we can text becomes a lead with the source Manual / Import.
          Patients who are already leads keep their status and source and only take their chart’s
          details, so this is safe to run again.
        </Text>
      </CrmModal>
    </>
  )
}

type CrmView = 'leads' | 'campaigns' | 'reports'

/**
 * The CRM (PD-1993): leads and where they came from, the campaigns sent to them, and the report
 * of how sources turn into patients — whose counts open the leads behind them.
 */
export function CrmPage() {
  const [view, setView] = useState<CrmView>('leads')
  // The report count the Leads view was opened from; null when it was opened directly.
  const [drill, setDrill] = useState<ReportDrill | null>(null)
  const switcher = (
    <SegmentedControl
      aria-label="CRM view"
      radius="md"
      value={view}
      onChange={(value) => {
        setDrill(null)
        setView(value as CrmView)
      }}
      data={[
        { value: 'leads', label: 'Leads' },
        { value: 'campaigns', label: 'Campaigns' },
        { value: 'reports', label: 'Reports' },
      ]}
    />
  )
  return (
    <div className="box-border min-h-full bg-[#F6F7F9] p-4 text-[#1F2733]">
      {view === 'leads' ? (
        <LeadsTab
          key={JSON.stringify(drill)}
          switcher={switcher}
          drill={drill}
          onBack={() => {
            setDrill(null)
            setView('reports')
          }}
        />
      ) : view === 'campaigns' ? (
        <CampaignsTab switcher={switcher} />
      ) : (
        <ReportsTab
          switcher={switcher}
          onOpenLeads={(next) => {
            setDrill(next)
            setView('leads')
          }}
        />
      )}
    </div>
  )
}

/** A filter the report applied, shown so it is never hidden, and removable. */
function ReportFilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex h-[30px] items-center gap-1.5 rounded-full border border-[#C9D8EA] bg-[#EEF4FB] pr-1 pl-3 text-[13px] font-semibold text-[#24548A]">
      {label}
      <button
        type="button"
        aria-label={`Remove filter ${label}`}
        onClick={onRemove}
        className="flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-[#24548A] hover:bg-[#DCE8F6]"
      >
        <IconX size={14} stroke={2.5} />
      </button>
    </span>
  )
}

/** One-click status views above the table — the design's saved audiences, over the statuses we keep. */
function StatusViews({
  value,
  onChange,
}: {
  value: CrmLeadStatus | null
  onChange: (value: CrmLeadStatus | null) => void
}) {
  const views: { value: CrmLeadStatus | null; label: string }[] = [
    { value: null, label: 'All leads' },
    ...CRM_LEAD_STATUSES.map((status) => ({ value: status, label: LEAD_STATUS_LABELS[status] })),
  ]
  return (
    <div role="group" aria-label="Status" className="flex flex-wrap gap-2">
      {views.map((view) => {
        const active = view.value === value
        return (
          <button
            key={view.label}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(view.value)}
            className={`h-[30px] cursor-pointer rounded-full border px-3 text-[13px] font-semibold transition-colors ${
              active
                ? 'border-[#316DAB] bg-[#EEF4FB] text-[#24548A]'
                : 'border-[#E4E7EB] bg-white text-[#4A5160] hover:bg-[#F6F7F9]'
            }`}
          >
            {view.label}
          </button>
        )
      })}
    </div>
  )
}

function LeadsTab({
  switcher,
  drill,
  onBack,
}: {
  switcher: ReactNode
  /** The report count this view was opened from, if any. */
  drill: ReportDrill | null
  onBack: () => void
}) {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search.trim(), 300)
  const [status, setStatus] = useState<CrmLeadStatus | null>(drill?.status ?? null)
  const [source, setSource] = useState<CrmLeadSource | null>(drill?.source ?? null)
  const [state, setState] = useState<string | null>(null)
  const [postalCode, setPostalCode] = useState('')
  const [debouncedPostalCode] = useDebouncedValue(postalCode, 300)
  // From a report count: the ad campaign that brought the leads in (null for untagged), the
  // SMS/email campaign that reached them, and how far they got.
  const [campaign, setCampaign] = useState<string | null | undefined>(drill?.campaign)
  const [outreach, setOutreach] = useState(drill?.outreach)
  const [milestone, setMilestone] = useState<LeadMilestone | undefined>(drill?.milestone)
  const [page, setPage] = useState(1)
  // The open lead. It follows the list while the lead is in it; a status change can take it out
  // of the view, and the dialog — with any unsaved notes — stays open on the saved lead.
  const [opened, setOpened] = useState<Lead | null>(null)

  const offset = (page - 1) * LEAD_PAGE_SIZE
  const leads = useCrmLeads({
    search: debouncedSearch || undefined,
    status: status ?? undefined,
    source: source ?? undefined,
    state: state ?? undefined,
    postalCode: debouncedPostalCode || undefined,
    campaign,
    outreach: outreach?.id,
    milestone,
    page: { offset, count: LEAD_PAGE_SIZE, limit: offset + LEAD_PAGE_SIZE },
  })
  const rows = leads.data?.data ?? []
  const total = leads.data?.page.total ?? 0
  const pages = Math.ceil(total / LEAD_PAGE_SIZE)
  // Only a newer copy: until a save's refetch lands, the list still holds the lead before it.
  const listed = opened && rows.find((lead) => lead.id === opened.id)
  if (listed && listed.updatedAt > opened.updatedAt) setOpened(listed)
  // The last lead on the last page left the view: step back to the new last page.
  const emptiedPage = Boolean(leads.data) && !rows.length && page > 1
  useEffect(() => {
    if (emptiedPage) setPage(Math.max(1, pages))
  }, [emptiedPage, pages])
  const filtered = Boolean(
    debouncedSearch ||
    status ||
    source ||
    state ||
    postalCode ||
    campaign !== undefined ||
    outreach ||
    milestone,
  )

  const resetPage =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value)
      setPage(1)
    }

  return (
    <CrmCard
      icon={<IconSpeakerphone size={22} className="text-[#E5533D]" stroke={2} />}
      count={leads.data ? total : undefined}
      switcher={switcher}
      tools={
        <>
          <CrmSearchInput
            label="Search"
            placeholder="Search name, phone or email"
            value={search}
            onChange={resetPage(setSearch)}
            className="w-[300px] max-w-full"
          />
          <Select
            aria-label="Source"
            placeholder="Any source"
            radius="md"
            clearable
            leftSection={<IconFilter size={16} className="text-[#6B7280]" />}
            data={CRM_LEAD_SOURCES.map((value) => ({ value, label: LEAD_SOURCE_LABELS[value] }))}
            value={source}
            onChange={(value) => resetPage(setSource)(value as CrmLeadSource | null)}
            className="w-48"
          />
          <Select
            aria-label="State"
            placeholder="Any state"
            radius="md"
            searchable
            clearable
            leftSection={<IconMapPin size={16} className="text-[#6B7280]" />}
            data={stateOptions}
            value={state}
            onChange={resetPage(setState)}
            className="w-44"
          />
          <TextInput
            aria-label="ZIP"
            placeholder="ZIP"
            radius="md"
            inputMode="numeric"
            maxLength={5}
            value={postalCode}
            onChange={(event) =>
              resetPage(setPostalCode)(event.currentTarget.value.replace(/\D/g, '').slice(0, 5))
            }
            className="w-24"
          />
        </>
      }
      action={
        <div className="flex flex-wrap items-center gap-2">
          <ImportPatientsButton />
          <NewLeadButton onCreated={setOpened} />
        </div>
      }
    >
      {drill && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex w-fit cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-sm font-semibold text-[#24548A] hover:underline"
        >
          <IconArrowLeft size={16} stroke={2.25} />
          Back to reports
        </button>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <StatusViews value={status} onChange={resetPage(setStatus)} />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {campaign !== undefined && (
            <ReportFilterChip
              label={`From: ${campaign ?? 'Unknown'}`}
              onRemove={() => resetPage(setCampaign)(undefined)}
            />
          )}
          {outreach && (
            <ReportFilterChip
              label={`Reached by: ${outreach.name}`}
              onRemove={() => resetPage(setOutreach)(undefined)}
            />
          )}
          {milestone && (
            <ReportFilterChip
              label={`${LEAD_MILESTONE_LABELS[milestone]}${outreach ? ' since sent' : ''}`}
              onRemove={() => resetPage(setMilestone)(undefined)}
            />
          )}
          {filtered && (
            <button
              type="button"
              onClick={() => {
                setSearch('')
                setStatus(null)
                setSource(null)
                setState(null)
                setPostalCode('')
                setCampaign(undefined)
                setOutreach(undefined)
                setMilestone(undefined)
                setPage(1)
              }}
              className="inline-flex h-[30px] cursor-pointer items-center gap-1.5 rounded-full border border-[#F3C6BD] bg-white px-3 text-[13px] font-semibold text-[#C2412E] transition hover:bg-[#FDECE8]"
            >
              <IconX size={14} stroke={2.5} />
              Clear filters
            </button>
          )}
        </div>
      </div>

      {leads.isError ? (
        <Alert color="red" variant="light" title="Couldn’t load the leads">
          <Button size="xs" variant="light" color="red" onClick={() => leads.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : leads.isPending ? (
        <div className="flex justify-center py-16">
          <Loader size="sm" aria-label="Loading leads" />
        </div>
      ) : rows.length === 0 ? (
        <p className="py-16 text-center text-sm text-[#6B7280]">
          {filtered
            ? 'No leads match these filters.'
            : 'No leads yet. A lead appears when someone confirms their phone at registration or books through Zocdoc. Add one with New lead, or import your existing patients.'}
        </p>
      ) : (
        <Table.ScrollContainer minWidth={1000}>
          <Table horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr className="h-10 border-b border-[#EAECEF]">
                <Table.Th className={tableHeaderClass}>Lead</Table.Th>
                <Table.Th className={tableHeaderClass}>Status</Table.Th>
                <Table.Th className={tableHeaderClass}>DOB</Table.Th>
                <Table.Th className={tableHeaderClass}>State</Table.Th>
                <Table.Th className={tableHeaderClass}>ZIP</Table.Th>
                <Table.Th className={tableHeaderClass}>Source</Table.Th>
                <Table.Th className={tableHeaderClass}>Added</Table.Th>
                <Table.Th className={tableHeaderClass}>Notes</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((lead) => (
                <Table.Tr
                  key={lead.id}
                  onClick={() => setOpened(lead)}
                  className={`${tableRowClass} ${lead.id === opened?.id ? 'bg-[#F5F8FC]' : ''}`}
                  aria-label={`Open ${leadName(lead)}`}
                >
                  <Table.Td>
                    <div className="flex items-center gap-3">
                      <LeadAvatar lead={lead} />
                      <div className="min-w-0">
                        <NameButton name={leadName(lead)} onClick={() => setOpened(lead)} />
                        <div className="text-xs text-[#6B7280]">
                          {[lead.phone, lead.email].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    </div>
                  </Table.Td>
                  <Table.Td>
                    <LeadStatusMenu lead={lead} />
                  </Table.Td>
                  <Table.Td className="whitespace-nowrap">
                    {lead.birthDate ? birthDateLabel(lead.birthDate) : <EmptyCell />}
                  </Table.Td>
                  <Table.Td>{lead.state ?? <EmptyCell />}</Table.Td>
                  <Table.Td>{lead.postalCode ?? <EmptyCell />}</Table.Td>
                  <Table.Td>
                    <TouchCell touch={lead.original} />
                  </Table.Td>
                  <Table.Td className="whitespace-nowrap">
                    {dateFormat.format(new Date(lead.createdAt))}
                  </Table.Td>
                  <Table.Td>
                    <NotesCell lead={lead} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {pages > 1 && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-[#6B7280]">
          <span>
            Page {page} of {pages}
          </span>
          <Pagination
            size="sm"
            radius="md"
            value={page}
            onChange={setPage}
            total={pages}
            className="ml-auto"
          />
        </div>
      )}

      <LeadModal lead={opened} onClose={() => setOpened(null)} onChanged={setOpened} />
    </CrmCard>
  )
}
